import {
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import aleloLogo from '@/assets/alelo-logo.svg'
import styles from './AleloLayout.module.css'
import fields from './AleloLayout.fields.module.css'

type Props = {
  children: ReactNode
  /** Formulário de duas colunas precisa de mais largura que o card padrão. */
  wide?: boolean
}

export function AleloLogo() {
  return (
    <div className={styles.logoWrap}>
      <img
        src={aleloLogo}
        alt="Alelo"
        className={styles.logoImg}
        width={120}
        height={83}
        decoding="async"
      />
    </div>
  )
}

export function AleloLayout({ children, wide = false }: Props) {
  return (
    <main className={styles.page}>
      <div className={[styles.column, wide ? styles.columnWide : '']
        .filter(Boolean)
        .join(' ')}
      >
        <AleloLogo />
        {children}
      </div>
    </main>
  )
}

/**
 * Button Rango DS.
 * LG = 64px (`$size-8x`), raio padrão 16px (`$default-border-radius`),
 * sentence case (sem `text-transform`), sem gradiente.
 */
export function AleloButton({
  variant = 'primary',
  className,
  type = 'button',
  loading = false,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'link'
  loading?: boolean
}) {
  const variantClass =
    variant === 'link'
      ? fields.linkButton
      : variant === 'secondary'
        ? fields.secondaryButton
        : fields.primaryButton
  const cls = [variantClass, className].filter(Boolean).join(' ')
  return (
    <button
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {children}
    </button>
  )
}

type FieldProps = {
  label?: ReactNode
  /** Texto de apoio abaixo do campo (`$_support-text`). */
  supportText?: ReactNode
  error?: ReactNode
  id?: string
  className?: string
  /** Elemento dentro da caixa do input, à direita (ex.: olho da senha). */
  endAdornment?: ReactNode
}

/** Input Text Rango DS: 64px, label visível, support text e erro inline. */
export function AleloInput({
  label,
  supportText,
  error,
  id,
  className,
  endAdornment,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & FieldProps) {
  const autoId = useId()
  const inputId = id ?? autoId
  const supportId = supportText ? `${inputId}-support` : undefined
  const errorId = error ? `${inputId}-error` : undefined
  const describedBy =
    [rest['aria-describedby'], errorId, supportId].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className={[fields.field, className].filter(Boolean).join(' ')}>
      {label ? (
        <label
          className={fields.label}
          htmlFor={inputId}
          data-required={rest.required ? 'true' : undefined}
        >
          {label}
        </label>
      ) : null}
      <div className={fields.inputWrap}>
        <input
          id={inputId}
          className={fields.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {endAdornment}
      </div>
      {error ? (
        <p id={errorId} className={fields.fieldError} role="alert">
          {error}
        </p>
      ) : null}
      {supportText ? (
        <p id={supportId} className={fields.supportText}>
          {supportText}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Celular brasileiro: o `+55` fica fixo à esquerda, fora do que se digita, para
 * deixar explícito que a tela só aceita número do Brasil.
 */
export function AleloPhoneInput({
  label,
  id,
  className,
  error,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string
  error?: ReactNode
}) {
  const autoId = useId()
  const inputId = id ?? autoId
  const errorId = error ? `${inputId}-error` : undefined
  return (
    <div className={fields.field}>
      {label ? (
        <label className={fields.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <span className={styles.phoneWrap}>
        <span className={styles.phonePrefix} aria-hidden="true">
          +55
        </span>
        <input
          id={inputId}
          type="tel"
          inputMode="numeric"
          className={[fields.input, styles.phoneInput, className]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...rest}
        />
      </span>
      {error ? (
        <p id={errorId} className={fields.fieldError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Campo senha com ícone de olho (mostrar/ocultar). */
export function AleloPasswordInput({
  id,
  label,
  supportText,
  error,
  showLabel,
  hideLabel,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  showLabel: string
  hideLabel: string
} & Omit<FieldProps, 'id'>) {
  const autoId = useId()
  const inputId = id ?? autoId
  const [show, setShow] = useState(false)
  return (
    <AleloInput
      id={inputId}
      label={label}
      supportText={supportText}
      error={error}
      type={show ? 'text' : 'password'}
      className={[fields.passwordField, className].filter(Boolean).join(' ')}
      endAdornment={
        <button
          type="button"
          className={fields.eyeButton}
          aria-label={show ? hideLabel : showLabel}
          aria-controls={inputId}
          aria-pressed={show}
          onClick={() => setShow((v) => !v)}
        >
          <EyeIcon open={show} />
        </button>
      }
      {...rest}
    />
  )
}

export function AleloSelect({
  label,
  id,
  className,
  children,
  error,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode
  error?: ReactNode
}) {
  const autoId = useId()
  const selectId = id ?? autoId
  const errorId = error ? `${selectId}-error` : undefined
  return (
    <div className={[fields.field, className].filter(Boolean).join(' ')}>
      {label ? (
        <label className={fields.label} htmlFor={selectId}>
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        className={fields.select}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        {...rest}
      >
        {children}
      </select>
      {error ? (
        <p id={errorId} className={fields.fieldError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Toggle tri-state (undefined/'yes'/'no') — mesma semântica do
 * has_mother_name do profile-review pós-login. Clicar no botão já ativo
 * volta para undefined (permite desfazer). */
export function AleloYesNoToggle({
  id,
  legend,
  yesLabel,
  noLabel,
  value,
  onChange,
}: {
  id: string
  legend?: string
  yesLabel: string
  noLabel: string
  value: 'yes' | 'no' | undefined
  onChange: (value: 'yes' | 'no' | undefined) => void
}) {
  return (
    <fieldset className={fields.toggleField}>
      {legend ? <legend className={fields.toggleLegend}>{legend}</legend> : null}
      <div className={fields.toggleGroup} role="group" aria-label={legend}>
        <button
          type="button"
          id={`${id}-yes`}
          className={fields.toggleButton}
          data-active={value === 'yes' ? 'true' : undefined}
          aria-pressed={value === 'yes'}
          onClick={() => onChange(value === 'yes' ? undefined : 'yes')}
        >
          {yesLabel}
        </button>
        <button
          type="button"
          id={`${id}-no`}
          className={fields.toggleButton}
          data-active={value === 'no' ? 'true' : undefined}
          aria-pressed={value === 'no'}
          onClick={() => onChange(value === 'no' ? undefined : 'no')}
        >
          {noLabel}
        </button>
      </div>
    </fieldset>
  )
}

/**
 * Erro de formulário. `role="alert"` anuncia a mensagem ao leitor de tela;
 * para associação campo-a-erro use a prop `error` do input.
 */
export function AleloError({
  message,
  id,
}: {
  message?: ReactNode
  id?: string
}) {
  if (!message) return null
  return (
    <p id={id} className={fields.formAlert} role="alert">
      {message}
    </p>
  )
}

/** Ícone olho — herda a cor do botão (tokens de texto). */
export function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3 3l18 18"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M10.6 10.6a2 2 0 002.8 2.8M9.9 5.1A10.5 10.5 0 0121 12c-.5 1-1.2 2-2.1 2.9M6.1 6.1C4.6 7.4 3.5 9.1 3 12c1.7 4.1 5.5 7 9 7 1.3 0 2.6-.3 3.8-.9"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 12S6.5 5.5 12 5.5 21.5 12 21.5 12 17.5 18.5 12 18.5 2.5 12 2.5 12z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}
